import React, { useEffect, useRef, useState } from 'react';

const CANDIDATES_PER_BATCH = 10;

const getFirstValue = (row, keys) => {
  const key = keys.find((candidate) => row[candidate] !== undefined && row[candidate] !== null && row[candidate] !== '');
  return key ? row[key] : null;
};

const formatNumber = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';

  const absoluteNumber = Math.abs(number);
  const roundsToZeroAtTwoDecimals = absoluteNumber > 0 && absoluteNumber < 0.005;
  const fractionDigits = roundsToZeroAtTwoDecimals
    ? Math.min(20, Math.floor(-Math.log10(absoluteNumber)) + 2)
    : null;

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: fractionDigits ?? 0,
    maximumFractionDigits: fractionDigits ?? 2,
  }).format(number);
};

const getMargin = (company, method) => method === 'ncav'
  ? getFirstValue(company, ['ncav_mos', 'margin', 'margin_of_safety'])
  : getFirstValue(company, ['pfv_mos', 'epsx_mos', 'margin', 'margin_of_safety']);

const CompanyCard = ({ company, method }) => {
  const symbol = getFirstValue(company, ['ticker', 'symbol']) || '—';
  const name = getFirstValue(company, ['company_name', 'companyName', 'name']);
  const price = getFirstValue(company, ['current_price', 'currentPrice', 'price']);
  const currentValue = method === 'ncav'
    ? getFirstValue(company, ['ncavps', 'ncav', 'current_value', 'currentValue'])
    : getFirstValue(company, ['pfvps', 'epsx', 'current_value', 'currentValue']);
  const margin = getMargin(company, method);
  const currency = getFirstValue(company, ['currency', 'trading_currency']) || '—';
  const industry = getFirstValue(company, ['industry']);

  return (
    <article className="company-card">
      <div className="card-topline">
        <div className="company-identity">
          <div>
            <div className="symbol-line">
              <h3>{symbol}</h3>
              <span className="currency-badge">{currency}</span>
            </div>
            <p>{name || 'Company name unavailable'}</p>
          </div>
        </div>
        {industry && <span className="industry-badge">{industry}</span>}
      </div>

      <div className="valuation-grid">
        <div className="value-block">
          <span>Current price</span>
          <strong>{formatNumber(price)}</strong>
          <small>{currency}</small>
        </div>
        <div className="value-block">
          <span>Current value</span>
          <strong>{formatNumber(currentValue)}</strong>
          <small>{currency}</small>
        </div>
      </div>

      <div className="margin-row">
        <span>Margin of safety</span>
        <strong>{formatNumber(margin)}%</strong>
      </div>
    </article>
  );
};

const Table = ({ data, method, hasQuery }) => {
  const [visibleCount, setVisibleCount] = useState(CANDIDATES_PER_BATCH);
  const loadMoreRef = useRef(null);
  const dataLength = data?.length || 0;

  useEffect(() => {
    setVisibleCount(CANDIDATES_PER_BATCH);
  }, [data, method]);

  const hasMore = visibleCount < dataLength;

  useEffect(() => {
    const loadMoreElement = loadMoreRef.current;
    if (!hasMore || !loadMoreElement || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisibleCount((currentCount) => (
          Math.min(currentCount + CANDIDATES_PER_BATCH, dataLength)
        ));
      }
    }, { rootMargin: '160px 0px' });

    observer.observe(loadMoreElement);
    return () => observer.disconnect();
  }, [dataLength, hasMore]);

  if (!data || data.length === 0) {
    return (
      <div className="empty-state">
        <span aria-hidden="true">⌁</span>
        <h3>
          {hasQuery
            ? 'No matches. Try another symbol or company name.'
            : 'No opportunities available right now.'}
        </h3>
      </div>
    );
  }

  const visibleData = data.slice(0, visibleCount);

  return (
    <>
      <div className="company-grid">
        {visibleData.map((company, index) => (
          <CompanyCard
            key={`${company.ticker || company.symbol || 'company'}-${index}`}
            company={company}
            method={method}
          />
        ))}
      </div>

      {hasMore && (
        <button
          ref={loadMoreRef}
          type="button"
          className="load-more-trigger"
          onClick={() => setVisibleCount((currentCount) => (
            Math.min(currentCount + CANDIDATES_PER_BATCH, dataLength)
          ))}
        >
          Show 5 more rows
        </button>
      )}
    </>
  );
};

export { CANDIDATES_PER_BATCH, CompanyCard, formatNumber, getMargin };
export default Table;
